import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-limits-panel>` says about itself.
 *
 * The board Org Status is built from: every allowance an organisation has, grouped, each drawn as a
 * `<caos-usage-bar>`, with one line at the top saying whether anything is in trouble.
 *
 * That line is the reason the component exists. Forty limits read end to end is forty numbers; what
 * a person actually wants from the screen is the answer to "is anything about to run out", and the
 * summary gives it before they scan. It is computed with the same `usageIntent` the bars use, which
 * is deliberate — a panel that counted "3 near their limit" by its own rule would eventually
 * disagree with the bars underneath it, and a summary that contradicts its own evidence is worse
 * than no summary.
 *
 * It renders its own bars from the `limits` property rather than taking slotted children, for the
 * same reason: the summary must be able to count exactly what it is showing. A panel summarising
 * markup it does not control is a number and a picture free to drift apart.
 *
 * Groups appear in the order they first occur in `limits` and are never sorted. "Hourly, daily,
 * monthly" is the platform's own vocabulary and carries an ordering a reader already knows;
 * alphabetising it would put daily before hourly and throw that away.
 */
const docs: ComponentDocs = {
  aliases: ['org limits', 'quota board', 'limits dashboard', 'governor limits', 'allowances panel', 'capacity panel'],

  answers: [
    'show every limit an organisation has on one screen',
    'tell somebody at a glance whether anything is about to run out',
    'group allowances by the period they reset on',
    'show API, storage and seat usage together',
  ],

  placements: ['appPage', 'homePage', 'recordPage'],

  whenToUse:
    'When several finite allowances are read together and the question is about the SET — is anything ' +
    'under pressure — rather than about any one of them.',

  insteadUse: [
    {
      name: 'UsageBar',
      when: 'there is one allowance, or the bars sit in a layout of their own that the panel would fight.',
    },
    {
      name: 'DataTable',
      when:
        'the limits are being compared column by column, sorted or exported — a table is for reading ' +
        'values against each other, this panel is for spotting pressure.',
    },
  ],

  inputs: [
    {
      name: 'limits',
      type: 'json',
      description:
        'The allowances to draw: `{ label, used, maximum, group?, unit? }[]`. A property rather than ' +
        'an attribute because it is structured data. Setting it re-renders. Groups are drawn in the ' +
        'order they first appear and are never sorted.',
    },
    {
      name: 'warnAt',
      attribute: 'warn-at',
      type: 'number',
      description: 'Percentage at which a limit counts as approaching, for both the bars and the summary count.',
      default: 75,
    },
    {
      name: 'dangerAt',
      attribute: 'danger-at',
      type: 'number',
      description: 'Percentage at which a limit counts as at its limit, for both the bars and the summary count.',
      default: 90,
    },
    {
      name: 'emptyText',
      attribute: 'empty-text',
      type: 'text',
      description: 'What to say when there are no limits to draw.',
      default: 'No limits reported.',
    },
  ],

  state: [
    {
      name: 'pressure',
      type: '{ danger: number; warning: number; total: number }',
      description:
        'How many limits sit at each level, by the same rule the bars colour themselves with. Read it ' +
        'to drive a badge or a tab count elsewhere without recomputing the thresholds and risking a ' +
        'different answer.',
    },
  ],

  parts: [
    { part: 'panel', is: 'The outer column holding the summary and the groups.' },
    { part: 'summary', is: 'The one-line verdict above the groups.' },
    { part: 'group', is: 'One group section.' },
    { part: 'group-name', is: 'A group heading.' },
    { part: 'limit', is: 'Each usage bar the panel renders.' },
  ],

  accessibility:
    'The summary is a `role="status"` live region, so a refresh that moves the organisation into ' +
    'trouble is announced rather than only redrawn — the case where somebody is watching a long ' +
    'import and is not looking at this panel. Each group is a `<section>` labelled by its heading, so ' +
    'the bars inside it are reachable as a named group. The bars carry their own meter semantics; ' +
    'pressure is stated in the summary text as well as in colour.',

  examples: [
    {
      key: 'org-status',
      label: 'An organisation under pressure',
      summary:
        'The Org Status screen: every allowance grouped by the period it resets on, with one line at ' +
        'the top answering the only question anybody brings to the screen — is anything about to ' +
        'run out. The count is computed from the same rule that coloured the bars below it.',
      placement: {
        component: 'caos-limits-panel',
        inputs: {
          limits: [
            { label: 'API calls', used: 9600, maximum: 10000, group: 'Hourly', unit: 'calls' },
            { label: 'Streaming events', used: 800, maximum: 1000, group: 'Hourly', unit: 'events' },
            { label: 'Asynchronous jobs', used: 120, maximum: 250, group: 'Hourly', unit: 'jobs' },
            { label: 'Bulk jobs', used: 10, maximum: 500, group: 'Daily', unit: 'jobs' },
            { label: 'Outbound mail', used: 430, maximum: 1000, group: 'Daily', unit: 'messages' },
            { label: 'File storage', used: 42, maximum: 100, group: 'Monthly', unit: 'GB' },
          ],
        },
      },
      stage: { needsWidth: 560 },
    },
    {
      key: 'all-healthy',
      label: 'Nothing to worry about',
      summary:
        'The same panel with room to spare. The summary says so in words rather than leaving a reader ' +
        'to conclude it from an absence of red.',
      placement: {
        component: 'caos-limits-panel',
        inputs: {
          limits: [
            { label: 'API calls', used: 1200, maximum: 10000, group: 'Hourly', unit: 'calls' },
            { label: 'Bulk jobs', used: 10, maximum: 500, group: 'Daily', unit: 'jobs' },
          ],
        },
      },
      stage: { needsWidth: 560 },
    },
    {
      key: 'ungrouped',
      label: 'Limits with no period',
      summary:
        'Allowances that do not reset on a cycle sit in a section with no heading rather than under an ' +
        'invented one — the panel does not know what to call a group the caller did not name.',
      placement: {
        component: 'caos-limits-panel',
        inputs: {
          limits: [
            { label: 'Permission set licences', used: 48, maximum: 50, unit: 'seats' },
            { label: 'Custom objects', used: 210, maximum: 400 },
          ],
        },
      },
      stage: { needsWidth: 560 },
    },
  ],
};

export default docs;
